import {Dispatch, FC, useEffect, useState} from 'react';
import Modal from 'react-modal';
import {Button} from '../common/Button';
import {getOperatoriesForFilters} from '../../api/operatories';
import {getAllTreatments} from '../../api/treatments';
import {
  OperatoryListProps,
  QueryParamsProps,
  TreatmentResponseProps
} from '../../constants/types';
import Spinner from '../common/Spinner';
import Error from '../common/Error';
import ExpandableTextFilterBlock from './ExpandableTextFilterBlock';
import {
  AreBlocksExpandedProps,
  AvailableFiltersProps,
  FiltersBodyProps
} from './types';
import ExpandableDateFilterBlock from './ExpandableDateFilterBlock';
import {convertDateTimeToUTC} from './helpers';
import url from 'url';
import {useHistory, useLocation} from 'react-router-dom';
import dayjs from 'dayjs';
import SwipeableDrawer from '@mui/material/SwipeableDrawer';
import IconButton from '@mui/material/IconButton';
import CloseIcon from '@mui/icons-material/Close';

const applyBtnLabel = 'Apply';
const clearBtnLabel = 'Clear';

Modal.setAppElement(document.getElementById('root'));

type CallsSheetListFilterProps = {
  isOpen: boolean;
  setFiltersBody: Dispatch<FiltersBodyProps>;
  query: QueryParamsProps;
  openFilters: () => void;
  closeFilters: () => void;
}

const wantsSoonerData = [
  {id: 1, value: 'Yes'},
  {id: 2, value: 'No'}
];

const wantsSoonerMapping = {
  '1': true,
  '2': false
};

const speciallyHandledFilter = 'WANT_SOONER';

const initialAvailableFilters = {
  PROCEDURE_TYPE: [],
  APPOINTMENT_AFTER: null,
  APPOINTMENT_BEFORE: null,
  OPERATORY: [],
  WANT_SOONER: []
};

const initialAreBlocksExpanded = {
  PROCEDURE_TYPE: false,
  APPOINTMENT: false,
  OPERATORY: false,
  WANT_SOONER: false
};

type LocationParams = {
  pathname: string,
  state?: {
    initialFilters?: Dictionary<string>
  }
}

export const CallsSheetListFilter: FC<CallsSheetListFilterProps> = ({
  isOpen,
  setFiltersBody,
  query,
  openFilters,
  closeFilters
}) => {
  const {pathname, state}: LocationParams = useLocation();
  const {initialFilters} = state || {};
  const hasInitialFilters = initialFilters &&
    !!Object.keys(initialFilters)?.length;

  const history = useHistory();
  const [areBlocksExpanded, setAreBlocksExpanded] =
    useState<AreBlocksExpandedProps>(initialAreBlocksExpanded);
  const [isLoading, setIsLoading] = useState(true);
  const [operatoriesData, setOperatoriesData] =
    useState<OperatoryListProps[] | null>(null);
  const [treatmentsData, setTreatmentsData] =
    useState<TreatmentResponseProps[] | null>(null);
  const [error, setError] = useState('');
  const [allFilters, setAllFilters] =
    useState<AvailableFiltersProps>({
      ...initialAvailableFilters,
      ...(hasInitialFilters && initialFilters)
    });

  const getFilters = async () => {
    setIsLoading(true);

    try {
      const [operatoriesResponse, treatmentsResponse] = await Promise.all([
        getOperatoriesForFilters(),
        getAllTreatments()
      ]);

      setOperatoriesData(operatoriesResponse);
      setTreatmentsData(treatmentsResponse);
    } catch (error) {
      setError(error?.message || '');
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    getFilters();
  }, []);

  const formFiltersRequestBody = () => {
    const newFiltersBody: FiltersBodyProps = [];

    Object.keys(allFilters).map((key) => {
      if (Array.isArray(allFilters[key]) && allFilters[key]?.length) {
        if (key === speciallyHandledFilter && allFilters[key]?.length === 1) {
          newFiltersBody.push({
            type: key,
            value: wantsSoonerMapping[allFilters[key][0]]
          });
        } else if (key !== speciallyHandledFilter) {
          newFiltersBody.push({type: key, values: allFilters[key]});
        }
      } else if (allFilters[key] instanceof Date &&
        !!allFilters[key] && dayjs(allFilters[key]).isValid()
      ) {
        newFiltersBody.push({
          type: key,
          value: convertDateTimeToUTC(allFilters[key])
        });
      }
    });

    return newFiltersBody;
  };

  return (
    <SwipeableDrawer
      anchor="right"
      open={isOpen}
      onClose={closeFilters}
      onOpen={openFilters}
      classes={{
        paper: 'calls-sheet-filter'
      }}
    >
      <div>
        <div className="button-container">
          <IconButton className="icon-button small blue" onClick={closeFilters}>
            <CloseIcon fontSize="small" className="icon" />
          </IconButton>
        </div>

        {isLoading && !error ?
          <Spinner loadingText="Getting available filters"/> :
          error ? <Error message={error} /> :
            <>
              <div className="filters-heading">
                <span className="bold-text">Filters</span>
                <Button
                  label={clearBtnLabel}
                  size="small"
                  variant="text"
                  onClick={() => {
                    setAllFilters(initialAvailableFilters);
                  }}/>
              </div>

              <ExpandableTextFilterBlock
                data={treatmentsData}
                filterItem={{
                  title: 'Procedure Type',
                  idKey: 'rootId',
                  valueKey: 'displayName',
                  filterType: 'PROCEDURE_TYPE'
                }}
                allFilters={allFilters}
                setAllFilters={setAllFilters}
                areBlocksExpanded={areBlocksExpanded}
                setAreBlocksExpanded={setAreBlocksExpanded}
              />

              <ExpandableDateFilterBlock
                filterItem={{
                  title: 'Appointment Date',
                  filterType: 'APPOINTMENT'
                }}
                allFilters={allFilters}
                setAllFilters={setAllFilters}
                areBlocksExpanded={areBlocksExpanded}
                setAreBlocksExpanded={setAreBlocksExpanded}
              />

              <ExpandableTextFilterBlock
                data={operatoriesData}
                filterItem={{
                  title: 'Operatory',
                  idKey: 'id',
                  valueKey: 'number',
                  filterType: 'OPERATORY'
                }}
                allFilters={allFilters}
                setAllFilters={setAllFilters}
                areBlocksExpanded={areBlocksExpanded}
                setAreBlocksExpanded={setAreBlocksExpanded}
              />

              <ExpandableTextFilterBlock
                data={wantsSoonerData}
                filterItem={{
                  title: 'Wants Sooner',
                  idKey: 'id',
                  valueKey: 'value',
                  filterType: 'WANT_SOONER'
                }}
                allFilters={allFilters}
                setAllFilters={setAllFilters}
                areBlocksExpanded={areBlocksExpanded}
                setAreBlocksExpanded={setAreBlocksExpanded}
              />

              <div className="button-container">
                <Button
                  label={applyBtnLabel}
                  color="success"
                  onClick={() => {
                    const shouldReturnToFirstPage =
                      query.page !== '1' || query.simple_search;

                    if (shouldReturnToFirstPage) {
                      history.push(url.format({
                        pathname, query: {...query, page: 1, simple_search: ''}
                      }));
                    }
                    setFiltersBody(formFiltersRequestBody());
                    closeFilters();
                  }}/>
              </div>
            </>}
      </div>
    </SwipeableDrawer>
  );
};
